import apiService from './apiService';
import { getApiBaseUrl } from '../config/apiConfig';
import { readVenteMagMatrixFromFile, readVenteSetMatrixFromFile, readVenteNewsletterMatrixFromFile, readVenteWebMatrixFromFile } from '../utils/kkpExcelImport';

const PREFIX_MAG = '/kkp/ventes/mag';
const PREFIX_SET = '/kkp/ventes/set';
const PREFIX_NEWSLETTER = '/kkp/ventes/newsletter';
const PREFIX_WEB = '/kkp/ventes/web';
const IMPORT_TIMEOUT_MS = 300000;
const SYNC_TIMEOUT_MS = 300000;
const LIST_TIMEOUT_MS = 120000;

async function request(promise) {
  const res = await promise;
  if (!res.success) {
    throw new Error(res.message || 'Erreur API');
  }
  return res;
}

async function downloadVenteExport(url, defaultFilename) {
  const token = localStorage.getItem('auth_token') || localStorage.getItem('jwt_token');
  const response = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}`, Accept: '*/*' } : { Accept: '*/*' },
  });

  const contentType = response.headers.get('Content-Type') || '';
  if (!response.ok) {
    if (contentType.includes('application/json')) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || `Erreur export (HTTP ${response.status})`);
    }
    throw new Error(`Erreur export (HTTP ${response.status})`);
  }
  if (contentType.includes('application/json')) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.message || 'Erreur export');
  }

  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition') || '';
  const match = disposition.match(/filename="?([^";]+)"?/);
  const filename = match?.[1] || defaultFilename;

  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}

const kkpService = {
  async getMagMeta() {
    const res = await request(apiService.get(`${PREFIX_MAG}/meta`));
    return res.data;
  },

  async getSetMeta() {
    const res = await request(apiService.get(`${PREFIX_SET}/meta`));
    return res.data;
  },

  async getNewsletterMeta() {
    const res = await request(apiService.get(`${PREFIX_NEWSLETTER}/meta`));
    return res.data;
  },

  async getWebMeta() {
    const res = await request(apiService.get(`${PREFIX_WEB}/meta`));
    return res.data;
  },

  async searchDolibarrClients(q, { limit = 15, dolibarrOnly = false, signal } = {}) {
    const res = await request(apiService.get('/kkp/clients/search', {
      q,
      limit,
      ...(dolibarrOnly ? { dolibarr_only: 1 } : {}),
    }, { signal }));
    return res.data.clients ?? [];
  },

  async getDolibarrClient(id) {
    const res = await request(apiService.get(`/kkp/clients/${id}`));
    return res.data.client;
  },

  async listVentesMag({
    annee = '',
    mois = '',
    commercial_id = '',
    format = '',
    contenu = '',
    statut_phase = '',
    search = '',
    view_mode = '',
    page = 1,
    limit = 50,
  } = {}) {
    const res = await request(apiService.get(PREFIX_MAG, {
      annee,
      mois,
      commercial_id,
      format,
      contenu,
      statut_phase,
      search,
      view_mode,
      page,
      limit,
    }));
    return {
      ventes: res.data.ventes ?? [],
      pagination: res.pagination ?? {},
      cdfValidated: Boolean(res.data.cdf_validated),
      viewModeCounts: res.data.view_mode_counts ?? { clients: 0, dispos: 0 },
    };
  },

  async listVentesSet({
    annee = '',
    mois = '',
    commercial_id = '',
    format = '',
    statut_phase = '',
    search = '',
    page = 1,
    limit = 50,
  } = {}) {
    const res = await request(apiService.get(PREFIX_SET, {
      annee,
      mois,
      commercial_id,
      format,
      statut_phase,
      search,
      page,
      limit,
    }));
    return { ventes: res.data.ventes ?? [], pagination: res.pagination ?? {} };
  },

  async listVentesNewsletter({
    annee = '',
    mois = '',
    semaine = '',
    date_debut = '',
    date_fin = '',
    commercial_id = '',
    emplacement = '',
    type_news = '',
    statut_phase = '',
    search = '',
    page = 1,
    limit = 50,
  } = {}) {
    const res = await request(apiService.get(PREFIX_NEWSLETTER, {
      annee,
      mois,
      semaine,
      date_debut,
      date_fin,
      commercial_id,
      emplacement,
      type_news,
      statut_phase,
      search,
      page,
      limit,
    }));
    return { ventes: res.data.ventes ?? [], pagination: res.pagination ?? {}, moyenne_hebdo: res.data.moyenne_hebdo ?? null };
  },

  async listVentesWeb({
    annee = '',
    mois = '',
    semaine = '',
    date_debut = '',
    date_fin = '',
    commercial_id = '',
    emplacement = '',
    statut_phase = '',
    search = '',
    page = 1,
    limit = 50,
  } = {}) {
    const res = await request(apiService.get(PREFIX_WEB, {
      annee,
      mois,
      semaine,
      date_debut,
      date_fin,
      emplacement,
      statut_phase,
      search,
      commercial_id,
      page,
      limit,
    }, { timeout: LIST_TIMEOUT_MS }));
    return { ventes: res.data.ventes ?? [], pagination: res.pagination ?? {}, moyenne_hebdo: res.data.moyenne_hebdo ?? null };
  },

  async getVenteMag(id) {
    const res = await request(apiService.get(`${PREFIX_MAG}/${id}`));
    return res.data.vente ?? null;
  },

  async createVenteMag(data) {
    const res = await request(apiService.post(PREFIX_MAG, data));
    return {
      vente: res.data.vente,
      notice: res.data.notice ?? null,
    };
  },

  async createVenteSet(data) {
    const res = await request(apiService.post(PREFIX_SET, data));
    return res.data.vente;
  },

  async createVenteNewsletter(data) {
    const res = await request(apiService.post(PREFIX_NEWSLETTER, data));
    return res.data.vente;
  },

  async createVenteWeb(data) {
    const res = await request(apiService.post(PREFIX_WEB, data));
    return {
      vente: res.data.vente ?? null,
      week_rows: res.data.week_rows ?? [],
      warnings: res.data.warnings ?? [],
      rejected: Boolean(res.data.rejected),
    };
  },

  async updateVenteMag(id, data) {
    const res = await request(apiService.put(`${PREFIX_MAG}/${id}`, data));
    return {
      vente: res.data.vente,
      notice: res.data.notice ?? null,
    };
  },

  async updateVenteSet(id, data) {
    const res = await request(apiService.put(`${PREFIX_SET}/${id}`, data));
    return res.data.vente;
  },

  async updateVenteNewsletter(id, data) {
    const res = await request(apiService.put(`${PREFIX_NEWSLETTER}/${id}`, data));
    return res.data.vente;
  },

  async updateVenteWeb(id, data) {
    const res = await request(apiService.put(`${PREFIX_WEB}/${id}`, data));
    return {
      vente: res.data.vente ?? null,
      week_rows: res.data.week_rows ?? [],
      warnings: res.data.warnings ?? [],
      rejected: Boolean(res.data.rejected),
    };
  },

  async deleteVenteMag(id) {
    await request(apiService.delete(`${PREFIX_MAG}/${id}`));
  },

  async clearVenteMag(id) {
    const res = await request(apiService.post(`${PREFIX_MAG}/${id}/clear`));
    return res.data.vente;
  },

  async deleteVenteSet(id) {
    await request(apiService.delete(`${PREFIX_SET}/${id}`));
  },

  async deleteVenteNewsletter(id) {
    await request(apiService.delete(`${PREFIX_NEWSLETTER}/${id}`));
  },

  async deleteVenteWeb(id) {
    const res = await request(apiService.delete(`${PREFIX_WEB}/${id}`));
    return { week_rows: res.data.week_rows ?? [] };
  },

  async clearVenteSet(id) {
    const res = await request(apiService.post(`${PREFIX_SET}/${id}/clear`));
    return res.data.vente;
  },

  async clearVenteNewsletter(id) {
    const res = await request(apiService.post(`${PREFIX_NEWSLETTER}/${id}/clear`));
    return res.data.vente;
  },

  async clearVenteWeb(id) {
    const res = await request(apiService.post(`${PREFIX_WEB}/${id}/clear`));
    return {
      vente: res.data.vente ?? null,
      week_rows: res.data.week_rows ?? [],
    };
  },

  async listPlanningAdmin() {
    const res = await request(apiService.get('/kkp/admin/planning'));
    return res.data;
  },

  async createPlanningMag(data) {
    const res = await request(apiService.post('/kkp/admin/planning/mag', data));
    return res.data.option;
  },

  async createPlanningSetFormat(data) {
    const res = await request(apiService.post('/kkp/admin/planning/set/formats', data));
    return res.data.option;
  },

  async createPlanningNewsletterType(data) {
    const res = await request(apiService.post('/kkp/admin/planning/newsletter/types', data));
    return res.data.option;
  },

  async createPlanningNewsletterEmplacement(data) {
    const res = await request(apiService.post('/kkp/admin/planning/newsletter/emplacements', data));
    return res.data.option;
  },

  async createNewsletterDispoRule(data) {
    await request(apiService.post('/kkp/admin/planning/newsletter/dispo-rules', data));
  },

  async deleteNewsletterDispoRule(id) {
    await request(apiService.delete(`/kkp/admin/planning/newsletter/dispo-rules/${id}`));
  },

  async createPlanningStatut(data) {
    const res = await request(apiService.post('/kkp/admin/planning/statuts', data));
    return res.data.option;
  },

  async deactivatePlanningOption(id, { year, month }) {
    const res = await request(apiService.post(`/kkp/admin/planning/options/${id}/deactivate`, {
      from_year: year,
      from_month: month,
    }));
    return res.data.option;
  },

  async reactivatePlanningOption(id) {
    const res = await request(apiService.post(`/kkp/admin/planning/options/${id}/reactivate`));
    return res.data.option;
  },

  async updatePlanningOption(id, data) {
    const res = await request(apiService.put(`/kkp/admin/planning/options/${id}`, data));
    return res.data.option;
  },

  async deletePlanningOption(id) {
    await request(apiService.delete(`/kkp/admin/planning/options/${id}`));
  },

  async listKlubOffersAdmin() {
    const res = await request(apiService.get('/kkp/admin/klub-offers'));
    return res.data.offers ?? [];
  },

  async upsertKlubOfferAdmin(data) {
    const res = await request(apiService.put('/kkp/admin/klub-offers', data));
    return res.data;
  },

  async deactivateKlubOfferAdmin(id, { year, month }) {
    const res = await request(apiService.post(`/kkp/admin/klub-offers/${id}/deactivate`, {
      from_year: year,
      from_month: month,
    }));
    return res.data;
  },

  async reactivateKlubOfferAdmin(id) {
    const res = await request(apiService.post(`/kkp/admin/klub-offers/${id}/reactivate`));
    return res.data;
  },

  async deleteKlubOfferAdmin(id) {
    await request(apiService.delete(`/kkp/admin/klub-offers/${id}`));
  },

  async listReferentielsMag() {
    const res = await request(apiService.get('/kkp/admin/referentiels/mag'));
    return res.data;
  },

  async createReferentielMag(data) {
    const res = await request(apiService.post('/kkp/admin/referentiels/mag', data));
    return res.data.option;
  },

  async updateReferentielMag(id, data) {
    const res = await request(apiService.put(`/kkp/admin/referentiels/mag/${id}`, data));
    return res.data.option;
  },

  async deleteReferentielMag(id) {
    await request(apiService.delete(`/kkp/admin/referentiels/mag/${id}`));
  },

  async listReferentielsNewsletter() {
    const res = await request(apiService.get('/kkp/admin/referentiels/newsletter'));
    return res.data;
  },

  async createNewsletterType(data) {
    const res = await request(apiService.post('/kkp/admin/referentiels/newsletter/types', data));
    return res.data.type;
  },

  async createNewsletterEmplacement(data) {
    const res = await request(apiService.post('/kkp/admin/referentiels/newsletter/emplacements', data));
    return res.data.emplacement;
  },

  async createNewsletterStatut(data) {
    const res = await request(apiService.post('/kkp/admin/referentiels/newsletter/statuts', data));
    return res.data.statut;
  },

  async deactivateNewsletterRef(id, { from_year, from_month }) {
    const res = await request(apiService.post(`/kkp/admin/referentiels/newsletter/${id}/deactivate`, {
      from_year,
      from_month,
    }));
    return res.data.option;
  },

  async reactivateNewsletterRef(id) {
    const res = await request(apiService.post(`/kkp/admin/referentiels/newsletter/${id}/reactivate`));
    return res.data.option;
  },

  async updateNewsletterRef(id, data) {
    const res = await request(apiService.put(`/kkp/admin/referentiels/newsletter/${id}`, data));
    return res.data.option;
  },

  async getCdfMag({ annee, mois } = {}) {
    const res = await request(apiService.get('/kkp/cdf/mag', { annee, mois }));
    return res.data;
  },

  async updateCdfMagConfig({ annee, mois, pageCount } = {}) {
    const res = await request(apiService.put('/kkp/cdf/mag/config', {
      annee,
      mois,
      page_count: pageCount,
    }));
    return res.data;
  },

  async reorderCdfMagDp({ annee, mois, spreadId, targetIndex, zone } = {}) {
    const res = await request(apiService.post('/kkp/cdf/mag/reorder-dp', {
      annee,
      mois,
      spread_id: spreadId,
      target_index: targetIndex,
      zone,
    }));
    return res.data;
  },

  async moveCdfMagPlacement({ annee, mois, venteId, spreadId, pageKey, slotId } = {}) {
    const body = {
      annee,
      mois,
      vente_id: venteId,
      spread_id: spreadId,
      page_key: pageKey,
    };
    if (slotId) {
      body.slot_id = slotId;
    }
    const res = await request(apiService.post('/kkp/cdf/mag/move', body));
    return res.data;
  },

  async saveCdfMagArticle({ annee, mois, pageKey, text } = {}) {
    const res = await request(apiService.put('/kkp/cdf/mag/article', {
      annee,
      mois,
      page_key: pageKey,
      text,
    }));
    return res.data;
  },

  async swapCdfMagPages({ annee, mois, sourcePageKey, targetPageKey } = {}) {
    const res = await request(apiService.post('/kkp/cdf/mag/swap-pages', {
      annee,
      mois,
      source_page_key: sourcePageKey,
      target_page_key: targetPageKey,
    }));
    return res.data;
  },

  async setCdfMagOptionalAgenda({ annee, mois, enabled } = {}) {
    const res = await request(apiService.put('/kkp/cdf/mag/optional-agenda', {
      annee,
      mois,
      enabled: Boolean(enabled),
    }));
    return res.data;
  },

  async setCdfMagMondeFoodLayout({ annee, mois, enabled } = {}) {
    const res = await request(apiService.put('/kkp/cdf/mag/config', {
      annee,
      mois,
      monde_food_layout: Boolean(enabled),
    }));
    return res.data;
  },

  async setCdfMagValidated({ annee, mois, validated } = {}) {
    const res = await request(apiService.put('/kkp/cdf/mag/validated', {
      annee,
      mois,
      validated,
    }));
    return res.data;
  },

  async getPubGraphiste({ tab = 'a_faire', annee, mois, search } = {}) {
    const params = { tab };
    if (annee !== undefined && annee !== null && annee !== '') {
      params.annee = annee;
    }
    if (mois !== undefined && mois !== null && mois !== '') {
      params.mois = mois;
    }
    if (search !== undefined && search !== null && String(search).trim() !== '') {
      params.search = String(search).trim();
    }
    const res = await request(apiService.get('/kkp/pub-graphiste', params));
    return res.data;
  },

  async getPubGraphisteAFaireCount() {
    const res = await request(apiService.get('/kkp/pub-graphiste', { count_only: 1 }));
    return res.data;
  },

  async deletePubGraphiste(id) {
    await request(apiService.delete(`/kkp/pub-graphiste/${id}`));
  },

  async marquerPubGraphisteTerminee(id, files = [], statutPhase) {
    const token = localStorage.getItem('auth_token') || localStorage.getItem('jwt_token');
    const formData = new FormData();
    files.forEach((file) => formData.append('files[]', file));
    if (statutPhase) {
      formData.append('statut_phase', statutPhase);
    }

    const response = await fetch(`${getApiBaseUrl()}/kkp/pub-graphiste/${id}/terminer`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data.success) {
      throw new Error(data.message || `Erreur validation (HTTP ${response.status})`);
    }

    return data.data;
  },

  async validerBat(id, files = []) {
    const token = localStorage.getItem('auth_token') || localStorage.getItem('jwt_token');
    const formData = new FormData();
    files.forEach((file) => formData.append('files[]', file));

    const response = await fetch(`${getApiBaseUrl()}/kkp/ventes/${id}/bat-valide`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data.success) {
      throw new Error(data.message || `Erreur validation BAT (HTTP ${response.status})`);
    }

    return data.data;
  },

  getCreaPubFichierUrl(venteId, fileId, { inline = false } = {}) {
    const params = inline ? '?inline=1' : '';
    return `${getApiBaseUrl()}/kkp/ventes/${venteId}/crea-pub/fichiers/${fileId}${params}`;
  },

  async fetchCreaPubFichierBlob(venteId, fileId, { inline = false } = {}) {
    const token = localStorage.getItem('auth_token') || localStorage.getItem('jwt_token');
    const response = await fetch(this.getCreaPubFichierUrl(venteId, fileId, { inline }), {
      headers: token ? { Authorization: `Bearer ${token}`, Accept: '*/*' } : { Accept: '*/*' },
    });

    if (!response.ok) {
      const contentType = response.headers.get('Content-Type') || '';
      if (contentType.includes('application/json')) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || `Erreur fichier (HTTP ${response.status})`);
      }
      throw new Error(`Erreur fichier (HTTP ${response.status})`);
    }

    return response.blob();
  },

  async downloadCreaPubFichier(venteId, fileId, defaultFilename) {
    const blob = await this.fetchCreaPubFichierBlob(venteId, fileId);
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = defaultFilename || 'fichier';
    link.click();
    URL.revokeObjectURL(link.href);
  },

  async uploadCreaPubFichier(venteId, file) {
    const token = localStorage.getItem('auth_token') || localStorage.getItem('jwt_token');
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${getApiBaseUrl()}/kkp/ventes/${venteId}/crea-pub/fichiers`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data.success) {
      throw new Error(data.message || `Erreur upload (HTTP ${response.status})`);
    }

    return data.data?.fichier ?? null;
  },

  async deleteCreaPubFichier(venteId, fileId) {
    const res = await request(apiService.delete(`/kkp/ventes/${venteId}/crea-pub/fichiers/${fileId}`));
    return res.data;
  },

  getBriefFichierUrl(venteId, fileId, { inline = false } = {}) {
    const params = inline ? '?inline=1' : '';
    return `${getApiBaseUrl()}/kkp/ventes/${venteId}/brief/fichiers/${fileId}${params}`;
  },

  async fetchBriefFichierBlob(venteId, fileId, { inline = false } = {}) {
    const token = localStorage.getItem('auth_token') || localStorage.getItem('jwt_token');
    const response = await fetch(this.getBriefFichierUrl(venteId, fileId, { inline }), {
      headers: token ? { Authorization: `Bearer ${token}`, Accept: '*/*' } : { Accept: '*/*' },
    });

    if (!response.ok) {
      const contentType = response.headers.get('Content-Type') || '';
      if (contentType.includes('application/json')) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || `Erreur fichier (HTTP ${response.status})`);
      }
      throw new Error(`Erreur fichier (HTTP ${response.status})`);
    }

    return response.blob();
  },

  async downloadBriefFichier(venteId, fileId, defaultFilename) {
    const blob = await this.fetchBriefFichierBlob(venteId, fileId);
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = defaultFilename || 'fichier';
    link.click();
    URL.revokeObjectURL(link.href);
  },

  async uploadBriefFichier(venteId, file) {
    const token = localStorage.getItem('auth_token') || localStorage.getItem('jwt_token');
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${getApiBaseUrl()}/kkp/ventes/${venteId}/brief/fichiers`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data.success) {
      throw new Error(data.message || `Erreur upload (HTTP ${response.status})`);
    }

    return data.data?.fichier ?? null;
  },

  async deleteBriefFichier(venteId, fileId) {
    const res = await request(apiService.delete(`/kkp/ventes/${venteId}/brief/fichiers/${fileId}`));
    return res.data;
  },

  async importVentesMag(file, { replace = false, replaceAll = false, upsert = false, annee = '', mois = '' } = {}) {
    const matrix = await readVenteMagMatrixFromFile(file);

    const body = {
      matrix,
      replace,
      replace_all: replaceAll,
      upsert,
    };
    if (replace && annee) body.annee = Number(annee);
    if (replace && mois) body.mois = Number(mois);

    const res = await request(apiService.post(`${PREFIX_MAG}/import-rows`, body, {}, { timeout: IMPORT_TIMEOUT_MS }));
    return res.data;
  },

  async importVentesSet(file, { replace = false, replaceAll = false, upsert = false, annee = '', mois = '' } = {}) {
    const matrix = await readVenteSetMatrixFromFile(file);

    const body = {
      matrix,
      replace,
      replace_all: replaceAll,
      upsert,
    };
    if (replace && annee) body.annee = Number(annee);
    if (replace && mois) body.mois = Number(mois);

    const res = await request(apiService.post(`${PREFIX_SET}/import-rows`, body, {}, { timeout: IMPORT_TIMEOUT_MS }));
    return res.data;
  },

  async importVentesNewsletter(file, { replace = false, replaceAll = false, upsert = false, annee = '', mois = '' } = {}) {
    const matrix = await readVenteNewsletterMatrixFromFile(file);

    const body = {
      matrix,
      replace,
      replace_all: replaceAll,
      upsert,
    };
    if (replace && annee) body.annee = Number(annee);
    if (replace && mois) body.mois = Number(mois);

    const res = await request(apiService.post(`${PREFIX_NEWSLETTER}/import-rows`, body, {}, { timeout: IMPORT_TIMEOUT_MS }));
    return res.data;
  },

  async importVentesWeb(file, { replace = false, replaceAll = false, replaceYear = false, upsert = false, annee = '', mois = '' } = {}) {
    const matrix = await readVenteWebMatrixFromFile(file);

    const body = {
      matrix,
      replace,
      replace_all: replaceAll,
      replace_year: replaceYear,
      upsert,
    };
    if ((replace || replaceYear) && annee) body.annee = Number(annee);
    if (replace && mois) body.mois = Number(mois);

    const res = await request(apiService.post(`${PREFIX_WEB}/import-rows`, body, {}, { timeout: IMPORT_TIMEOUT_MS }));
    return res.data;
  },

  async exportVentesMag({ annee = '', mois = '', format = 'xlsx' } = {}) {
    const params = new URLSearchParams();
    if (annee) params.set('annee', String(annee));
    if (mois) params.set('mois', String(mois));
    if (format) params.set('format', format);

    const url = `${getApiBaseUrl()}${PREFIX_MAG}/export?${params}`;
    await downloadVenteExport(url, `vente-mag.${format === 'csv' ? 'csv' : 'xlsx'}`);
  },

  async exportVentesSet({ annee = '', mois = '', format = 'xlsx' } = {}) {
    const params = new URLSearchParams();
    if (annee) params.set('annee', String(annee));
    if (mois) params.set('mois', String(mois));
    if (format) params.set('format', format);

    const url = `${getApiBaseUrl()}${PREFIX_SET}/export?${params}`;
    await downloadVenteExport(url, `vente-set.${format === 'csv' ? 'csv' : 'xlsx'}`);
  },

  async exportVentesNewsletter({ annee = '', mois = '', format = 'xlsx' } = {}) {
    const params = new URLSearchParams();
    if (annee) params.set('annee', String(annee));
    if (mois) params.set('mois', String(mois));
    if (format) params.set('format', format);

    const url = `${getApiBaseUrl()}${PREFIX_NEWSLETTER}/export?${params}`;
    await downloadVenteExport(url, `vente-newsletter.${format === 'csv' ? 'csv' : 'xlsx'}`);
  },

  async exportVentesWeb({ annee = '', mois = '', format = 'xlsx' } = {}) {
    const params = new URLSearchParams();
    if (annee) params.set('annee', String(annee));
    if (mois) params.set('mois', String(mois));
    if (format) params.set('format', format);

    const url = `${getApiBaseUrl()}${PREFIX_WEB}/export?${params}`;
    await downloadVenteExport(url, `vente-web.${format === 'csv' ? 'csv' : 'xlsx'}`);
  },

  async getBilanAudienceMeta(period = {}) {
    const res = await request(apiService.get('/kkp/bilan-audience/meta', period));
    return res.data;
  },

  async previewBilanAudience(period) {
    const res = await request(apiService.get('/kkp/bilan-audience/preview', period));
    return res.data;
  },

  async generateBilanAudience(payload) {
    const res = await request(apiService.post('/kkp/bilan-audience/generate', payload, {}, { timeout: 180000 }));
    return res.data;
  },

  async listPublications() {
    const res = await request(apiService.get('/kkp/publications'));
    return res.data ?? { mag: {}, set: {} };
  },

  async syncMailchimpAudience({ annee = '', mois = '' } = {}) {
    const body = {};
    if (annee) body.annee = Number(annee);
    if (mois) body.mois = Number(mois);
    const res = await request(apiService.post('/kkp/sync/mailchimp-audience', body));
    return res.data.sync;
  },

  async syncNewsletterAudience({ annee = '', mois = '' } = {}) {
    const body = {};
    if (annee) body.annee = Number(annee);
    if (mois) body.mois = Number(mois);
    const res = await request(apiService.post('/kkp/sync/newsletter-audience', body));
    return res.data.sync;
  },

  async syncBrevoAudience({ annee = '', mois = '' } = {}) {
    const body = {};
    if (annee) body.annee = Number(annee);
    if (mois) body.mois = Number(mois);
    const res = await request(apiService.post('/kkp/sync/brevo-audience', body));
    return res.data.sync;
  },

  async syncWebAudience({ annee = '', mois = '' } = {}) {
    const body = {};
    if (annee) body.annee = Number(annee);
    if (mois) body.mois = Number(mois);
    const res = await request(apiService.post('/kkp/sync/web-audience', body, {}, { timeout: SYNC_TIMEOUT_MS }));
    return res.data.sync;
  },

  async listAudienceSyncLogs({ canal = '', limit = 20 } = {}) {
    const res = await request(apiService.get('/kkp/sync/logs', { canal, limit }));
    return res.data.logs ?? [];
  },

  async testMailchimpConnection() {
    const res = await request(apiService.get('/kkp/sync/test/mailchimp'));
    return res.data;
  },

  async testBrevoConnection() {
    const res = await request(apiService.get('/kkp/sync/test/brevo'));
    return res.data;
  },

  async testWordPressConnection(siteUrl) {
    const res = await request(apiService.get('/kkp/sync/test/wordpress', { site_url: siteUrl }));
    return res.data.test;
  },

  async listUnmatchedMailchimpCampaigns({ annee = '' } = {}) {
    const res = await request(apiService.get('/kkp/sync/mailchimp/unmatched', {
      annee: annee || new Date().getFullYear(),
    }));
    return res.data.campaigns ?? [];
  },

  async listMailchimpCampaigns({ annee = '' } = {}) {
    const res = await request(apiService.get('/kkp/sync/mailchimp/campaigns', {
      annee: annee || new Date().getFullYear(),
    }));
    return res.data.campaigns ?? [];
  },

  async listMailchimpLinks() {
    const res = await request(apiService.get('/kkp/sync/mailchimp-links'));
    return res.data.links ?? [];
  },

  async linkMailchimpCampaign({ mailchimp_campaign_id, kkp_vente_id, campaign_title = '' }) {
    const res = await request(apiService.post('/kkp/sync/mailchimp-link', {
      mailchimp_campaign_id,
      kkp_vente_id,
      campaign_title,
    }));
    return res.data.link;
  },

  async unlinkMailchimpCampaign(linkId) {
    const res = await request(apiService.delete(`/kkp/sync/mailchimp-link/${linkId}`));
    return res.data;
  },

  async listUnmatchedBrevoCampaigns({ annee = '' } = {}) {
    const res = await request(apiService.get('/kkp/sync/brevo/unmatched', {
      annee: annee || new Date().getFullYear(),
    }));
    return res.data.campaigns ?? [];
  },

  async listBrevoCampaigns({ annee = '' } = {}) {
    const res = await request(apiService.get('/kkp/sync/brevo/campaigns', {
      annee: annee || new Date().getFullYear(),
    }));
    return res.data.campaigns ?? [];
  },

  async listBrevoLinks() {
    const res = await request(apiService.get('/kkp/sync/brevo-links'));
    return res.data.links ?? [];
  },

  async linkBrevoCampaign({ brevo_campaign_id, kkp_vente_id, campaign_title = '' }) {
    const res = await request(apiService.post('/kkp/sync/brevo-link', {
      brevo_campaign_id,
      kkp_vente_id,
      campaign_title,
    }));
    return res.data.link;
  },

  async unlinkBrevoCampaign(linkId) {
    const res = await request(apiService.delete(`/kkp/sync/brevo-link/${linkId}`));
    return res.data;
  },

  async listWebAds({ site_url = '', search = '', limit = 100 } = {}) {
    const res = await request(apiService.get('/kkp/sync/web/ads', {
      site_url,
      search,
      limit,
    }));
    return res.data.ads ?? [];
  },

  async listUnmatchedWebVentes({ annee = '' } = {}) {
    const res = await request(apiService.get('/kkp/sync/web/unmatched', {
      annee: annee || new Date().getFullYear(),
    }));
    return res.data.ventes ?? [];
  },

  async listWpLinks() {
    const res = await request(apiService.get('/kkp/sync/wp-links'));
    return res.data.links ?? [];
  },

  async linkWpAd({ wp_ad_id, kkp_vente_id, ad_title = '', site_url = '' }) {
    const res = await request(apiService.post('/kkp/sync/wp-link', {
      wp_ad_id,
      kkp_vente_id,
      ad_title,
      site_url,
    }));
    return res.data.link;
  },

  async unlinkWpAd(linkId) {
    const res = await request(apiService.delete(`/kkp/sync/wp-link/${linkId}`));
    return res.data;
  },

  async listCdfDispoAlertCrons() {
    const res = await request(apiService.get('/kkp/crons'));
    return res.data;
  },

  async createCdfDispoAlertCron(data) {
    const res = await request(apiService.post('/kkp/crons', data));
    return res.data.cron;
  },

  async updateCdfDispoAlertCron(id, data) {
    const res = await request(apiService.put(`/kkp/crons/${id}`, data));
    return res.data.cron;
  },

  async setCdfDispoAlertCronActive(id, active) {
    const res = await request(apiService.post(`/kkp/crons/${id}/active`, { active }));
    return res.data.cron;
  },

  async runCdfDispoAlertCron(id, filters = null) {
    const body = filters ? { filters } : {};
    const res = await request(apiService.post(`/kkp/crons/${id}/run`, body));
    return res.data.cron;
  },

  async deleteCdfDispoAlertCron(id) {
    await request(apiService.delete(`/kkp/crons/${id}`));
  },

  async getCronTypePermissions() {
    const res = await request(apiService.get('/kkp/crons/type-permissions'));
    return res.data;
  },

  async saveCronTypePermissions(matrix) {
    const res = await request(apiService.put('/kkp/crons/type-permissions', { matrix }));
    return res.data;
  },
};

export default kkpService;
